// src/pages/ClientBlogsPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiSearch } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { BlogService } from '../services/blog.service';
import type { BlogCategory, BlogPost } from '../types/Blog';

export default function ClientBlogsPage() {
  const { t, i18n } = useTranslation();

  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [q, setQ] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const isSpanish = i18n.language.startsWith('es');
  const allLabel = isSpanish ? 'Todas' : 'All';
  const subtitleDefault = isSpanish ? 'Artículos y novedades' : 'Articles and updates';

  const fetchPosts = (next?: { q?: string; categoryId?: string }) => {
    setLoading(true);
    BlogService.getPublicPosts({
      q: next?.q ?? q,
      categoryId: next?.categoryId ?? categoryId,
      limit: 20
    })
      .then(res => setPosts(Array.isArray(res) ? res : []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    BlogService.getPublicCategories()
      .then(res => setCategories(Array.isArray(res) ? res : []))
      .catch(() => setCategories([]));

    fetchPosts({ q: '', categoryId: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredCategory = useMemo(
    () => categories.find(c => String(c.id) === String(categoryId)) || null,
    [categories, categoryId]
  );

  const chips = useMemo(() => {
    const base: Array<{ id: string; name: string }> = [{ id: '', name: allLabel }];
    return base.concat(categories.map(c => ({ id: String(c.id), name: c.name })));
  }, [categories, allLabel]);

  const searchPlaceholder = `${t('common.search')}...`;

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: '24px 16px 32px' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: 0 }}>Blog</h1>
          <p style={{ margin: '8px 0 0', color: 'var(--color-texto-secundario)' }}>
            {filteredCategory ? `${t('blog.categories')}: ${filteredCategory.name}` : subtitleDefault}
          </p>
        </div>
      </header>

      <div
        style={{
          marginTop: 16,
          background: 'var(--color-blanco)',
          border: '1px solid rgba(0,0,0,0.06)',
          borderRadius: 24,
          padding: 16,
          boxShadow: 'var(--sombra-elevacion-1)'
        }}
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 14px',
              borderRadius: 18,
              border: '1px solid var(--color-gris-claro)',
              background: 'var(--color-fondo)',
              flex: '1 1 320px'
            }}
          >
            <FiSearch />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder={searchPlaceholder}
              style={{ border: 'none', outline: 'none', width: '100%', background: 'transparent' }}
              onKeyDown={e => {
                if (e.key === 'Enter') fetchPosts({ q: q.trim(), categoryId });
              }}
            />
          </div>

          <button
            type="button"
            onClick={() => fetchPosts({ q: q.trim(), categoryId })}
            style={{
              border: 'none',
              borderRadius: 18,
              padding: '12px 16px',
              background: 'var(--color-primario)',
              color: '#fff',
              fontWeight: 800,
              cursor: 'pointer',
              boxShadow: 'var(--sombra-elevacion-1)'
            }}
          >
            {t('common.search')}
          </button>
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
          {chips.map(c => {
            const active = String(categoryId) === String(c.id);
            return (
              <button
                key={c.id || '__all__'}
                type="button"
                onClick={() => {
                  setCategoryId(c.id);
                  fetchPosts({ q: q.trim(), categoryId: c.id });
                }}
                style={{
                  border: '1px solid rgba(0,0,0,0.08)',
                  background: active ? 'var(--color-secundario)' : 'rgba(255,255,255,0.9)',
                  color: active ? 'var(--color-primario-oscuro)' : 'var(--color-texto-principal)',
                  borderRadius: 999,
                  padding: '10px 14px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {c.name}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div style={{ marginTop: 22 }}>{t('common.loading')}</div>
      ) : posts.length === 0 ? (
        <div
          style={{
            marginTop: 22,
            background: 'var(--color-blanco)',
            border: '1px solid rgba(0,0,0,0.06)',
            borderRadius: 24,
            padding: 18,
            color: 'var(--color-texto-secundario)',
            boxShadow: 'var(--sombra-elevacion-1)'
          }}
        >
          {t('blog.empty_posts')}
        </div>
      ) : (
        <div
          style={{
            marginTop: 18,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 16
          }}
        >
          {posts.map(p => {
            const href = p.slug ? `/blogs/${p.slug}` : `/blogs/${p.id}`;
            return (
              <Link
                key={String(p.id)}
                to={href}
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  background: 'var(--color-blanco)',
                  border: '1px solid rgba(0,0,0,0.06)',
                  borderRadius: 24,
                  overflow: 'hidden',
                  boxShadow: 'var(--sombra-elevacion-2)',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {p.cover_image_url ? (
                  <img
                    src={p.cover_image_url}
                    alt={p.title}
                    style={{ width: '100%', height: 190, objectFit: 'cover', display: 'block' }}
                    onError={e => (e.currentTarget.style.display = 'none')}
                  />
                ) : (
                  <div style={{ height: 16 }} />
                )}

                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {p.categories?.length ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {p.categories.slice(0, 3).map(c => (
                        <span
                          key={String(c.id)}
                          style={{
                            fontSize: 12,
                            fontWeight: 900,
                            padding: '8px 12px',
                            borderRadius: 999,
                            background: 'var(--color-secundario)',
                            color: 'var(--color-primario-oscuro)'
                          }}
                        >
                          {c.name}
                        </span>
                      ))}
                    </div>
                  ) : null}

                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 950, lineHeight: 1.2 }}>{p.title}</h3>

                  <p
                    style={{
                      margin: 0,
                      color: 'var(--color-texto-secundario)',
                      lineHeight: 1.55,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {p.excerpt || ''}
                  </p>

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={e => {
                        e.preventDefault();
                        navigate(href);
                      }}
                      style={{
                        border: 'none',
                        borderRadius: 16,
                        padding: '10px 14px',
                        background: 'rgba(103,58,183,0.10)',
                        color: 'var(--color-primario-oscuro)',
                        fontWeight: 900,
                        cursor: 'pointer'
                      }}
                    >
                      {t('blog.view_full')}
                    </button>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
